import { useCallback, useEffect, useRef } from 'react';

import { fg } from '@confluence/feature-gating';

import {
	captureBylineLifecycle,
	type BylineLifecycle,
	type PresentationObservation,
} from './bylineLifecycleMetrics.ts';

type Source = PresentationObservation['source'];
type Binding = {
	active: boolean;
	recorder: BylineLifecycle;
	observed: Set<Source>;
};

// The containing runtime is keyed by contentId and apps by app.id. Each mounted app records
// only its first result per source; retries and StrictMode effects cannot inflate the counters.
type BeginObservation = (
	source: Source,
) =>
	| ((outcome: 'succeeded' | 'failed', titleChanged?: boolean, iconChanged?: boolean) => void)
	| undefined;

export const useBylinePresentationMetrics = (contentId: string | undefined): BeginObservation => {
	const binding = useRef<Binding | null>(null);
	const observations = useRef<{ contentId: string | undefined; sources: Set<Source> } | null>(null);
	useEffect(() => {
		if (!fg('confluence_byline_cls_telemetry')) return;
		if (!observations.current || observations.current.contentId !== contentId) {
			observations.current = { contentId, sources: new Set() };
		}
		const current: Binding = {
			active: true,
			recorder: captureBylineLifecycle(),
			observed: observations.current.sources,
		};
		binding.current = current;
		return () => {
			current.active = false;
		};
	}, [contentId]);

	return useCallback((source: Source) => {
		// Capture the binding at invocation, never when the async result returns.
		const current = binding.current;
		if (!current?.active || !current.recorder) return;
		return (outcome: 'succeeded' | 'failed', titleChanged = false, iconChanged = false) => {
			if (!current?.active || current.observed.has(source)) return;
			current.observed.add(source);
			current.recorder?.presentation({ source, outcome, titleChanged, iconChanged });
		};
	}, []);
};
